import { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Radio from '@mui/material/Radio';
import CloseIcon from '@mui/icons-material/Close';

/**
 * Pick a single Google calendar to associate with this vertical. New
 * component — not reusing the personal-tab CalendarPickerDialog.
 *
 * Props:
 *  - open: boolean
 *  - calendars: array of { id, summary, primary, backgroundColor }
 *  - initialSelectedId: string | null  // currently-saved calendar id
 *  - onClose(): cancel
 *  - onSelect(calendar: { id, summary, backgroundColor }): commit
 */
const SelectVerticalCalendarDialog = ({
  open,
  calendars,
  initialSelectedId,
  onClose,
  onSelect,
}) => {
  const [selectedId, setSelectedId] = useState(initialSelectedId || '');

  useEffect(() => {
    if (open) setSelectedId(initialSelectedId || '');
  }, [open, initialSelectedId]);

  const orderedCalendars = [
    ...calendars.filter((c) => c.primary),
    ...calendars.filter((c) => !c.primary),
  ];

  const cellBase = {
    padding: '8px 12px',
    fontSize: 13,
    color: '#344054',
    borderBottom: '1px solid #EAECF0',
    verticalAlign: 'middle',
  };

  const handleSelect = () => {
    const cal = orderedCalendars.find((c) => c.id === selectedId);
    if (!cal) return;
    onSelect({
      id: cal.id,
      summary: cal.summary,
      backgroundColor: cal.backgroundColor,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pr: 1,
        }}
      >
        Choose Which Calendar to Become This Verticalʼs Master Calendar
        <IconButton onClick={onClose} size="small" aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {orderedCalendars.length === 0 ? (
          <p style={{ margin: 0, color: '#667085' }}>No calendars found on your Google account.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th
                  style={{
                    ...cellBase,
                    textAlign: 'left',
                    fontWeight: 600,
                    color: '#475467',
                    background: '#F9FAFB',
                    width: 40,
                  }}
                />
                <th
                  style={{
                    ...cellBase,
                    textAlign: 'left',
                    fontWeight: 600,
                    color: '#475467',
                    background: '#F9FAFB',
                  }}
                >
                  Google Calendar Name
                </th>
              </tr>
            </thead>
            <tbody>
              {orderedCalendars.map((cal) => {
                const checked = selectedId === cal.id;
                return (
                  <tr
                    key={cal.id}
                    onClick={() => setSelectedId(cal.id)}
                    style={{ cursor: 'pointer', textAlign: 'left' }}
                  >
                    <td style={{ ...cellBase, width: 40 }}>
                      <Radio
                        checked={checked}
                        onChange={() => setSelectedId(cal.id)}
                        size="small"
                      />
                    </td>
                    <td style={cellBase}>
                      <span
                        style={{
                          display: 'inline-block',
                          width: 12,
                          height: 12,
                          borderRadius: 3,
                          background: cal.backgroundColor || '#356AC3',
                          marginRight: 8,
                          verticalAlign: 'middle',
                        }}
                      />
                      <span title={cal.summary}>
                        {cal.summary}
                        {cal.primary && (
                          <span style={{ color: '#667085', marginLeft: 6 }}>(primary)</span>
                        )}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSelect} disabled={!selectedId}>
          Select
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SelectVerticalCalendarDialog;
